import type { Node, Edge } from '@xyflow/react';
import type {
  UnifiedDiagramData,
  UnifiedDiagramNode,
  UnifiedDiagramEdge,
} from '@veodiagram/shared';
import { getNodeColors, edgeStyles } from './nodeStyles';

/**
 * Convert UnifiedDiagramNode to React Flow Node.
 */
export function convertToReactFlowNode(
  node: UnifiedDiagramNode,
  isDark: boolean = false
): Node {
  const colors = getNodeColors(node.type, isDark);

  return {
    id: node.id,
    type: mapNodeTypeToFlowType(node.type),
    position: node.position,
    data: {
      label: node.label,
      description: node.description,
      nodeType: node.type,
      technology: node.technology,
      tags: node.tags,
      ...node.data,
    },
    style: {
      width: node.width,
      height: node.height,
      backgroundColor: node.style?.backgroundColor || colors.bg,
      borderColor: node.style?.borderColor || colors.border,
      color: node.style?.textColor || colors.text,
      borderWidth: node.style?.borderWidth || 2,
      borderStyle: node.style?.borderStyle || 'solid',
    },
    parentId: node.parentId,
  };
}

/**
 * Convert UnifiedDiagramEdge to React Flow Edge.
 */
export function convertToReactFlowEdge(edge: UnifiedDiagramEdge): Edge {
  const edgeStyle = edgeStyles[edge.type] || edgeStyles.default;

  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    label: edge.label,
    type: mapEdgeTypeToFlowType(edge.type),
    animated: edge.animated || edgeStyle.animated || false,
    style: {
      stroke: edge.style?.strokeColor || edgeStyle.color,
      strokeWidth: edge.style?.strokeWidth || edgeStyle.strokeWidth,
      strokeDasharray: edge.style?.strokeDasharray || edgeStyle.strokeDasharray,
    },
    data: {
      ...edge.data,
      edgeType: edge.type,
    },
  };
}

/**
 * Map extended node type to React Flow node type.
 * This maps to the custom node components available.
 */
function mapNodeTypeToFlowType(nodeType: string): string {
  // Map to existing custom node types
  switch (nodeType) {
    case 'deployment':
    case 'softwareSystem':
      return 'deployment';

    case 'database':
    case 'storage':
    case 'cache':
    case 'dataStore':
      return 'storage';

    case 'queue':
      return 'queue';

    case 'codeBlock':
      return 'codeBlock';

    // Issue #500: Import graph node type
    case 'importGraphNode':
      return 'importGraphNode';

    // Issue #414: Call graph node types for multi-handle support
    case 'function':
      return 'function';
    case 'method':
      return 'method';
    case 'arrow':
      return 'arrow';
    case 'constructor':
      return 'constructor';
    case 'entry':
      return 'entry';
    case 'critical':
      return 'critical';
    case 'hotspot':
      return 'hotspot';
    case 'callGraphNode':
      return 'callGraphNode';

    case 'service':
    case 'api':
    case 'gateway':
    case 'frontend':
    case 'container':
    case 'component':
    case 'process':
    case 'server':
    case 'loadBalancer':
    case 'cdn':
    case 'firewall':
    case 'external':
    case 'externalEntity':
    case 'person':
    case 'entity':
    case 'trustBoundary':
    case 'decision':
    case 'start':
    case 'end':
    default:
      return 'service';
  }
}

/**
 * Map extended edge type to React Flow edge type.
 * Uses smart edge types with intelligent label positioning.
 */
function mapEdgeTypeToFlowType(edgeType: string): string {
  switch (edgeType) {
    case 'http':
    case 'sync':
    case 'calls':
    case 'uses':
    case 'reads':
    case 'writes':
    case 'publishes':
    case 'subscribes':
    case 'dependency':
      return 'smartSmoothStep';
    case 'inheritance':
    case 'composition':
    case 'aggregation':
      return 'smartSmoothStep';
    case 'async':
    case 'bidirectional':
      return 'smartBezier';
    default:
      return 'smartSmoothStep';
  }
}

/**
 * Convert UnifiedDiagramData to React Flow nodes and edges.
 */
export function convertToReactFlow(
  data: UnifiedDiagramData,
  isDark: boolean = false
): { nodes: Node[]; edges: Edge[] } {
  const nodes = data.nodes.map((node: UnifiedDiagramNode) => convertToReactFlowNode(node, isDark));
  const edges = data.edges.map((edge: UnifiedDiagramEdge) => convertToReactFlowEdge(edge));

  return { nodes, edges };
}

/**
 * Convert React Flow Node back to UnifiedDiagramNode.
 */
export function convertFromReactFlowNode(node: Node): UnifiedDiagramNode {
  return {
    id: node.id,
    type: (node.data?.nodeType as UnifiedDiagramNode['type']) || 'service',
    label: (node.data?.label as string) || '',
    description: node.data?.description as string | undefined,
    position: node.position,
    width: typeof node.style?.width === 'number' ? node.style.width : undefined,
    height: typeof node.style?.height === 'number' ? node.style.height : undefined,
    style: {
      backgroundColor: typeof node.style?.backgroundColor === 'string' ? node.style.backgroundColor : undefined,
      borderColor: typeof node.style?.borderColor === 'string' ? node.style.borderColor : undefined,
      textColor: typeof node.style?.color === 'string' ? node.style.color : undefined,
    },
    technology: node.data?.technology as string | undefined,
    tags: node.data?.tags as string[] | undefined,
    parentId: node.parentId,
    data: node.data as Record<string, unknown> | undefined,
  };
}

/**
 * Convert React Flow Edge back to UnifiedDiagramEdge.
 */
export function convertFromReactFlowEdge(edge: Edge): UnifiedDiagramEdge {
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    label: typeof edge.label === 'string' ? edge.label : undefined,
    type: (edge.data?.edgeType as UnifiedDiagramEdge['type']) || 'default',
    animated: edge.animated || false,
    style: {
      strokeColor: typeof edge.style?.stroke === 'string' ? edge.style.stroke : undefined,
      strokeWidth: typeof edge.style?.strokeWidth === 'number' ? edge.style.strokeWidth : undefined,
      strokeDasharray: typeof edge.style?.strokeDasharray === 'string' ? edge.style.strokeDasharray : undefined,
    },
    data: edge.data as Record<string, unknown> | undefined,
  };
}

/**
 * Convert React Flow state back to UnifiedDiagramData.
 */
export function convertFromReactFlow(
  nodes: Node[],
  edges: Edge[],
  metadata: UnifiedDiagramData['metadata']
): UnifiedDiagramData {
  return {
    metadata,
    nodes: nodes.map(convertFromReactFlowNode),
    edges: edges.map(convertFromReactFlowEdge),
  };
}
